import { FC, useMemo } from "react";
import { useChartOfAccountsTemplateAccounts } from "@api/coaTemplate.api";
import { ApplyCoaTemplatePreviewCoa } from "@components/settings/ChartOfAccountsTemplates/apply/ApplyCoaTemplatePreviewCoa";
import { TemplateDrawerTrail } from "@components/settings/ChartOfAccountsTemplates/TemplateDrawerTrail";
import { Drawer } from "@design-system/Drawer/Drawer";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { Loader } from "@design-system/Loader/Loader";
import { templateToPreviewEntity } from "./coaPreviewMappers";
import styles from "./CoaPreviewDrawer.module.scss";

interface Props {
    open: boolean;
    templateId: string | null;
    templateName?: string;
    onClose: () => void;
}

// Templates aren't bound to an entity; entityId is only a label/key here, so any
// value works.
const SYNTHETIC_TEMPLATE_ENTITY_ID = 0;

export const CoaPreviewDrawer: FC<Props> = ({
    open,
    templateId,
    templateName,
    onClose,
}) => {
    const templateAccounts = useChartOfAccountsTemplateAccounts({
        templateId: templateId ?? undefined,
        enabled: open && templateId !== null,
    });

    const previewEntity = useMemo(() => {
        if (!templateAccounts.data) {
            return null;
        }
        return templateToPreviewEntity({
            entityId: SYNTHETIC_TEMPLATE_ENTITY_ID,
            accounts: templateAccounts.data.accounts,
            groups: templateAccounts.data.groups,
        });
    }, [templateAccounts.data]);

    const hasAccounts = (previewEntity?.accounts.length ?? 0) > 0;

    const renderPreviewBody = () => {
        if (templateAccounts.isLoading) {
            return (
                <div className={styles.placeholder}>
                    <Loader />
                </div>
            );
        }
        if (previewEntity && hasAccounts) {
            return <ApplyCoaTemplatePreviewCoa entity={previewEntity} />;
        }
        return (
            <div className={styles.placeholder}>
                <EmptyState header="No accounts to preview" />
            </div>
        );
    };

    return (
        <Drawer
            open={open}
            onClose={onClose}
            size="fullScreen"
            contentClassName={styles.drawer}
            noPadding
            headerAddonLeft={
                <TemplateDrawerTrail
                    title="Preview template"
                    templateName={templateName}
                />
            }
        >
            <div className={styles.drawerContent}>{renderPreviewBody()}</div>
        </Drawer>
    );
};
